import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import type {
  TrustScoreSignalRow,
  TrustScoreSignalsResponse,
} from '../services/trustScoreService';

// FEAT-043-admin-web (MVP-8.9, MVP-18.24).
// Renders the raw `TrustScoreSignal` rows the admin is permitted to
// inspect after a successful audited read. The table is paginated via
// the admin signals endpoint — no client-side sorting/filtering, which
// keeps the admin's actions auditable (each page is a fresh, audited
// server call carrying the read-reason captured by the panel).

const SIGNAL_TYPE_COLOR: Record<
  TrustScoreSignalRow['signalType'],
  'primary' | 'secondary' | 'success' | 'warning' | 'error'
> = {
  INTERACTION_RATING: 'primary',
  BOOKING_RATING: 'success',
  MESSAGE_HEURISTIC: 'secondary',
  BEHAVIORAL_PATTERN: 'warning',
  REPORT_DISPOSITION: 'error',
};

function formatSignalType(type: TrustScoreSignalRow['signalType']): string {
  switch (type) {
    case 'INTERACTION_RATING':
      return 'Interaction';
    case 'BOOKING_RATING':
      return 'Booking';
    case 'MESSAGE_HEURISTIC':
      return 'Message';
    case 'BEHAVIORAL_PATTERN':
      return 'Behavioral';
    case 'REPORT_DISPOSITION':
      return 'Report';
    default: {
      // Exhaustive switch guard per workspace TS rules.
      const _exhaustive: never = type;
      return _exhaustive;
    }
  }
}

function formatValue(value: number): string {
  return value.toFixed(2);
}

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export interface SignalsTableProps {
  loading: boolean;
  data: TrustScoreSignalsResponse | null;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}

export const SignalsTable: React.FC<SignalsTableProps> = ({
  loading,
  data,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const signals = data?.signals ?? [];
  const total = data?.pagination?.total ?? 0;

  return (
    <Paper variant="outlined" data-testid="trust-score-signals-table">
      <TableContainer>
        <Table size="small" aria-label="Trust score raw signals">
          <TableHead>
            <TableRow>
              <TableCell>Captured</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Subtype</TableCell>
              <TableCell align="right">Value</TableCell>
              <TableCell align="right">Weight</TableCell>
              <TableCell>Source</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : signals.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Stack alignItems="center" spacing={1} sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No signals recorded for this user yet.
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Signals accrue as the user interacts with the platform.
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : (
              signals.map((signal) => (
                <TableRow key={signal.id} hover>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {formatTimestamp(signal.capturedAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={SIGNAL_TYPE_COLOR[signal.signalType] ?? 'default'}
                      label={formatSignalType(signal.signalType)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{signal.signalSubtype}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontFamily="monospace">
                      {formatValue(signal.value)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontFamily="monospace">
                      {formatValue(signal.weight)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>
                    {signal.sourceRefType ? (
                      <Stack spacing={0.25}>
                        <Typography variant="caption" color="text.secondary">
                          {signal.sourceRefType}
                        </Typography>
                        <Typography
                          variant="caption"
                          fontFamily="monospace"
                          sx={{ wordBreak: 'break-all' }}
                        >
                          {signal.sourceRefId ?? '—'}
                        </Typography>
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={Math.max(page - 1, 0)}
        onPageChange={(_, newPage) => onPageChange(newPage + 1)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          const next = parseInt(event.target.value, 10);
          if (!Number.isNaN(next)) {
            onRowsPerPageChange(next);
          }
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Signals per page:"
      />
    </Paper>
  );
};

export default SignalsTable;
